/**
 * Recorded-figure history for one Open Ledger company.
 *
 * Pure normalization/grouping over the registry's per-period `figures` map:
 * rows keep their original period token, reported unit and period-end date,
 * and are grouped by exact unit + nominal period kind (annual / quarter /
 * half-year) + revenue basis only. Unlike the scoreboard — which groups by an
 * exact periodEnd for cross-company comparison — a history group retains many
 * dates, ordered chronologically.
 *
 * What this module never does: convert units, merge rows across units or
 * period kinds, infer missing values, or claim anything about statement
 * duration, standalone-vs-YTD basis or accounting comparability. Reported
 * labels alone do not verify any of that. Missing periods stay missing (never
 * zero) and malformed rows are diagnosed, never guessed.
 */

import type { RevenueBasis } from "@/config/open-ledger-legacy-charts";
import { isMillionsUnit } from "@/lib/open-ledger-conventions";
import {
  figureRevenueBasis,
  reportedPeriodKind,
  type ReportedPeriodKind,
} from "@/lib/open-ledger-scoreboard";
import type { OpenLedgerCompany } from "@/lib/open-ledger.types";

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

type HistoryOmitReason =
  | "invalid-amounts"
  | "unrecognized-unit"
  | "unrecognized-period"
  | "invalid-period-end";

/** Why a company has no usable history at all. */
type HistoryUnavailableReason = "no-recorded-figures";

export interface HistoryGroupKey {
  unit: string;
  kind: ReportedPeriodKind;
  /**
   * What the group's revenue column measures. Part of the key so a net-revenue
   * row never shares a series with headline-revenue rows.
   */
  revenueBasis: RevenueBasis;
}

/** One recorded figure row, in the registry's own reported terms. */
export interface HistoryRow {
  /** Original registry period token (`FY2026Q2`, …) — a label, not a duration. */
  period: string;
  kind: ReportedPeriodKind;
  unit: string;
  revenue: number;
  netIncome: number;
  /** Transaction date = fiscal period end (YYYY-MM-DD). */
  periodEnd: string;
  /** `"revenue"` unless the registry record says `"net-revenue"`. */
  revenueBasis: RevenueBasis;
  /**
   * Issuer filing this row was reconciled against — present on records
   * carrying registry provenance (`legacy-chart` or `house-chart`).
   */
  filing?: string;
}

export interface HistoryGroup extends HistoryGroupKey {
  /**
   * Stable machine id: `unit|kind`, with a `|net-revenue` suffix for a
   * net-revenue series (headline-revenue ids are unchanged).
   */
  id: string;
  /** Chronological by periodEnd, ties broken by period token. */
  rows: HistoryRow[];
}

export interface HistoryInvalidRow {
  /** Original period token as keyed in the figures map. */
  period: string;
  reason: HistoryOmitReason;
}

/** Provenance available on the company record (hub-side fields). */
export interface HistoryProvenance {
  ledgerUrl: string;
  repo: string;
  lastSyncedCommit: string;
  lastUpdated: string;
}

interface HistoryBase {
  slug: string;
  provenance: HistoryProvenance;
  /**
   * Inventory periods with no recorded figure row, in inventory (oldest-first)
   * order. Gaps stay explicit — never zero, never inferred.
   */
  missingPeriods: string[];
}

export interface AvailableCompanyHistory extends HistoryBase {
  status: "available";
  /** Deterministic: id ascending. */
  groups: HistoryGroup[];
  /** Figure rows that failed validation, in period-token order. */
  invalidRows: HistoryInvalidRow[];
}

export interface UnavailableCompanyHistory extends HistoryBase {
  status: "unavailable";
  reason: HistoryUnavailableReason;
}

type CompanyHistory = AvailableCompanyHistory | UnavailableCompanyHistory;

export function historyGroupId(key: HistoryGroupKey): string {
  const base = `${key.unit}|${key.kind}`;
  return key.revenueBasis === "revenue" ? base : `${base}|${key.revenueBasis}`;
}

/**
 * Default group: the longest recorded series; ties keep id order. Shared by
 * the hub's history panel and the SSR company page (w1/m233), which renders
 * this group open and the rest collapsed.
 */
export function defaultHistoryGroup(
  groups: readonly HistoryGroup[],
): HistoryGroup | null {
  let best: HistoryGroup | null = null;
  for (const group of groups) {
    if (best == null || group.rows.length > best.rows.length) best = group;
  }
  return best;
}

/** Chronological by periodEnd, ties broken by period token. */
function compareHistoryRows(a: HistoryRow, b: HistoryRow): number {
  return (
    a.periodEnd.localeCompare(b.periodEnd) || a.period.localeCompare(b.period)
  );
}

/**
 * Group rows by exact unit + nominal kind. Groups are id-ascending, rows
 * chronological — the single row model shared by the build and truncation
 * paths so they cannot drift apart.
 */
function groupHistoryRows(rows: readonly HistoryRow[]): HistoryGroup[] {
  const byId = new Map<string, HistoryGroup>();
  for (const row of rows) {
    const id = historyGroupId(row);
    let group = byId.get(id);
    if (!group) {
      group = {
        unit: row.unit,
        kind: row.kind,
        revenueBasis: row.revenueBasis,
        id,
        rows: [],
      };
      byId.set(id, group);
    }
    group.rows.push(row);
  }
  const groups = [...byId.values()].sort((a, b) => a.id.localeCompare(b.id));
  for (const group of groups) {
    group.rows.sort(compareHistoryRows);
  }
  return groups;
}

function classifyRow(
  period: string,
  figures: {
    revenue: unknown;
    netIncome: unknown;
    unit: unknown;
    periodEnd: unknown;
    revenueBasis?: RevenueBasis;
    provenance?: { filing: string };
  },
): { ok: true; row: HistoryRow } | { ok: false; reason: HistoryOmitReason } {
  const { revenue, netIncome, unit, periodEnd } = figures;
  if (
    typeof revenue !== "number" ||
    !Number.isFinite(revenue) ||
    typeof netIncome !== "number" ||
    !Number.isFinite(netIncome)
  ) {
    return { ok: false, reason: "invalid-amounts" };
  }
  if (typeof unit !== "string" || !isMillionsUnit(unit)) {
    return { ok: false, reason: "unrecognized-unit" };
  }
  const kind = reportedPeriodKind(period);
  if (kind == null) return { ok: false, reason: "unrecognized-period" };
  if (typeof periodEnd !== "string" || !DATE_RE.test(periodEnd)) {
    return { ok: false, reason: "invalid-period-end" };
  }
  return {
    ok: true,
    row: {
      period,
      kind,
      unit,
      revenue,
      netIncome,
      periodEnd,
      revenueBasis: figureRevenueBasis(figures),
      ...(figures.provenance ? { filing: figures.provenance.filing } : {}),
    },
  };
}

/**
 * Build the recorded history for one company. Does not mutate `company`.
 *
 * `status: "unavailable"` means the registry carries no figure rows for the
 * company at all — every inventory period is then listed under
 * `missingPeriods`. Otherwise groups hold the valid rows and `invalidRows`
 * diagnoses the rest; both lists can only be reconciled against
 * `missingPeriods` by the caller keeping all three visible.
 */
export function buildCompanyHistory(company: OpenLedgerCompany): CompanyHistory {
  const provenance: HistoryProvenance = {
    ledgerUrl: company.ledgerUrl,
    repo: company.repo,
    lastSyncedCommit: company.lastSyncedCommit,
    lastUpdated: company.lastUpdated,
  };
  const figures = company.figures;
  const missingPeriods = company.periods.filter(
    (period) => figures?.[period] === undefined,
  );
  if (figures == null || Object.keys(figures).length === 0) {
    return {
      status: "unavailable",
      slug: company.slug,
      provenance,
      reason: "no-recorded-figures",
      missingPeriods,
    };
  }

  const rows: HistoryRow[] = [];
  const invalidRows: HistoryInvalidRow[] = [];
  for (const period of Object.keys(figures).sort((a, b) => a.localeCompare(b))) {
    const result = classifyRow(period, figures[period]);
    if (!result.ok) {
      invalidRows.push({ period, reason: result.reason });
      continue;
    }
    rows.push(result.row);
  }
  const groups = groupHistoryRows(rows);

  return {
    status: "available",
    slug: company.slug,
    provenance,
    groups,
    missingPeriods,
    invalidRows,
  };
}

// ---------------------------------------------------------------------------
// Lazy server-boundary contract (w1/m178/t002).
//
// The hub's initial payload stays latest-figures-only; one company's bounded
// history loads on demand through `getOpenLedgerCompanyHistory`. Everything
// here is serializable (no Dates, Maps or class instances) so it crosses the
// server-function boundary unchanged.
// ---------------------------------------------------------------------------

/**
 * Hard cap on history rows per response. The live tree carries only a
 * handful of rows per company — far below this cap, so it never truncates
 * today — but a response must stay bounded however large a `figures` map
 * grows. Truncation keeps the newest rows and names every dropped period in
 * `omittedPeriods`; known available periods are never silently omitted.
 */
export const COMPANY_HISTORY_MAX_ROWS = 100;

/** Lowercase slug shape (`acme-co`): what registry keys look like. */
const HISTORY_SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * Strict slug-format check for the request validator. Rejects anything shaped
 * like a filesystem path (`../x`, `/abs`, `a/b`) or a remote URL
 * (`https://…`) before any lookup — and the lookup itself is an exact
 * in-memory key match, never a path join or fetch, so a malformed slug that
 * reached it still could not escape the registry.
 */
export function isHistorySlug(value: unknown): value is string {
  return typeof value === "string" && HISTORY_SLUG_RE.test(value);
}

export interface BoundedAvailableCompanyHistory extends AvailableCompanyHistory {
  /**
   * True only when the cap dropped rows. `totalRows` then exceeds the
   * returned rows and `omittedPeriods` names the dropped (oldest) periods,
   * oldest first.
   */
  truncated: boolean;
  /** Valid rows before the cap. */
  totalRows: number;
  /** Empty unless `truncated`. */
  omittedPeriods: string[];
}

/** Known-format slug with no registry entry — distinct from "no figures". */
export interface UnknownCompanyHistory {
  status: "unknown-company";
  /** Echo of the validated request slug. */
  slug: string;
}

export type CompanyHistoryResponse =
  | BoundedAvailableCompanyHistory
  | UnavailableCompanyHistory
  | UnknownCompanyHistory;

/**
 * Apply the row cap to a built history. Unavailable histories have no rows
 * and pass through unchanged; available histories gain the truncation fields.
 */
export function boundCompanyHistory(
  history: CompanyHistory,
  maxRows: number = COMPANY_HISTORY_MAX_ROWS,
): BoundedAvailableCompanyHistory | UnavailableCompanyHistory {
  if (history.status === "unavailable") return history;
  const totalRows = history.groups.reduce(
    (sum, group) => sum + group.rows.length,
    0,
  );
  if (totalRows <= maxRows) {
    return { ...history, truncated: false, totalRows, omittedPeriods: [] };
  }
  const chronological = history.groups
    .flatMap((group) => group.rows)
    .sort(compareHistoryRows);
  const omitted = chronological.slice(0, chronological.length - maxRows);
  const kept = chronological.slice(chronological.length - maxRows);
  const groups = groupHistoryRows(kept);
  return {
    ...history,
    groups,
    truncated: true,
    totalRows,
    omittedPeriods: omitted.map((row) => row.period),
  };
}

/**
 * Resolve one company's bounded history from loaded showcase companies.
 * Pure over the in-memory list: the slug is matched exactly against
 * `company.slug`, so unknown and malformed inputs can only yield the
 * `unknown-company` result — never a filesystem or network lookup.
 */
export function selectCompanyHistory(
  companies: readonly OpenLedgerCompany[],
  slug: string,
): CompanyHistoryResponse {
  const company = companies.find((candidate) => candidate.slug === slug);
  if (!company) return { status: "unknown-company", slug };
  return boundCompanyHistory(buildCompanyHistory(company));
}
